/**
 * AdoraCare -- tooth diagram view model (TOOTH-NOTATION-INTERIM-V1). Pure,
 * React-free helpers behind the tappable odontogram (founder ruling R2: the
 * tap grid is the PRIMARY September entry; typed entry is assistive only).
 *
 * SOURCE OF TRUTH: the reference set arrives on the doctor consult-context
 * wire (server-resolved from THE ONE lib/domain/clinical/tooth-reference.ts
 * module) -- this module DERIVES layout (arch rows, viewer order) from the
 * served rows' FDI digits and never mints its own tooth vocabulary. The
 * display token maps EXPLICITLY through the served fdiCode/universalCode
 * pair -- no client-side notation translation (clinical safety ruling).
 *
 * LAYOUT CONVENTION (standard dental chart, viewer perspective): the
 * patient's UPPER RIGHT quadrant renders on the chart's LEFT. Upper row:
 * 18..11 | 21..28 (primary 55..51 | 61..65). Lower row: 48..41 | 31..38
 * (primary 85..81 | 71..75).
 */

import type {
  CaseSheetClinicalView,
  ToothNotationDisplayView,
  ToothReferenceView,
  ToothRefView,
} from '../api';

export type ToothDentitionView = 'permanent' | 'primary';

/**
 * The context the tooth-entry surfaces need, derived ONCE from the consult
 * context. TWO INDEPENDENT FACTS, deliberately separated by
 * DOCTOR-PRECALL-POLISH-V1 (founder walk 2026-08-04, the diagram-render fix):
 *
 *   references  -- the RENDER grain. The served canonical vocabulary. It is
 *                  the built-in module set (api/routes/doctor-workspace.ts
 *                  serves TOOTH_REFERENCES, a code constant), so it needs NO
 *                  table and NO seed to render.
 *   storageLive -- the WRITE grain. Whether `clinical_tooth_refs` is live so
 *                  a selection can be SAVED (the server's toothRefsAvailable
 *                  probe / sheet read).
 *
 * PREVIOUSLY both collapsed into one null: a storage/deploy gap silently
 * removed the whole diagram from all three grains, and a doctor cannot tell
 * "your environment is behind" from "this product has no tooth chart"
 * (DR-04 / DR-23 finding). The chart now ALWAYS renders when the vocabulary
 * is served; when storage is not live it renders LOCKED and says so, and the
 * free-text Tooth/area path stays the savable one. The original law -- never
 * a diagram that silently cannot save -- is kept by the lock + the honest
 * line, not by a blank.
 *
 * null = the vocabulary itself was not served (an older server), the ONE
 * case where no chart can be drawn at all.
 */
export interface ToothEntryContext {
  readonly references: readonly ToothReferenceView[];
  readonly display: ToothNotationDisplayView;
  /** Whether a selection can be SAVED. false = render locked + say so. */
  readonly storageLive: boolean;
}

/** The honest line a locked chart carries -- visible degradation, never a
 *  silent blank (the product's own honest-chip pattern). */
export const TOOTH_STORAGE_UNAVAILABLE_HINT =
  'Read-only chart - tooth selection cannot be saved in this environment yet.';

export function toothEntryContextFrom(context: {
  readonly toothReferences?: readonly ToothReferenceView[];
  readonly toothNotationDisplay?: ToothNotationDisplayView;
  readonly toothRefsAvailable?: boolean;
}): ToothEntryContext | null {
  const references = context.toothReferences ?? [];
  if (references.length === 0) return null;
  return {
    references,
    display: context.toothNotationDisplay ?? 'fdi',
    storageLive: context.toothRefsAvailable === true,
  };
}

/** The display token for one tooth under the org display choice -- always
 *  the SERVED mapping, never a client computation. */
export function toothDisplayToken(
  ref: Pick<ToothReferenceView, 'fdiCode' | 'universalCode'>,
  display: ToothNotationDisplayView,
): string {
  return display === 'universal' ? ref.universalCode : ref.fdiCode;
}

/** The ONE render shape: token + canonical name (a raw code is never
 *  rendered alone -- design contract item 6). */
export function toothDisplayLabel(
  ref: Pick<ToothReferenceView, 'fdiCode' | 'universalCode' | 'canonicalName'>,
  display: ToothNotationDisplayView,
): string {
  return `${toothDisplayToken(ref, display)} - ${ref.canonicalName}`;
}

/** One arch row of the diagram in VIEWER order (see the layout convention). */
export interface ToothDiagramArch {
  readonly arch: 'upper' | 'lower';
  readonly teeth: readonly ToothReferenceView[];
}

const QUADRANT_ORDER: Readonly<
  Record<ToothDentitionView, { upper: readonly [string, string]; lower: readonly [string, string] }>
> = {
  permanent: { upper: ['1', '2'], lower: ['4', '3'] },
  primary: { upper: ['5', '6'], lower: ['8', '7'] },
};

function archRow(
  references: readonly ToothReferenceView[],
  quadrants: readonly [string, string],
): readonly ToothReferenceView[] {
  const [rightDigit, leftDigit] = quadrants;
  const right = references
    .filter((t) => t.fdiCode.startsWith(rightDigit))
    .sort((a, b) => b.fdiCode.localeCompare(a.fdiCode)); // distal -> mesial
  const left = references
    .filter((t) => t.fdiCode.startsWith(leftDigit))
    .sort((a, b) => a.fdiCode.localeCompare(b.fdiCode)); // mesial -> distal
  return [...right, ...left];
}

/** Both arch rows for one dentition, in viewer order. Pure. */
export function toothDiagramArches(
  references: readonly ToothReferenceView[],
  dentition: ToothDentitionView,
): readonly ToothDiagramArch[] {
  const order = QUADRANT_ORDER[dentition];
  return [
    { arch: 'upper', teeth: archRow(references, order.upper) },
    { arch: 'lower', teeth: archRow(references, order.lower) },
  ];
}

// --- Quadrant grouping (DOCTOR-OS-BUILD-PHASE-1-V1, mockup 04 frame D1) -----
//
// The v2 chart composition renders each arch row as its TWO FDI quadrant
// groups with the standard quadrant names, so a first-time user orients
// without prose. Names derive from the ISO 3950 / FDI quadrant digits ONLY
// (adult 1-4, primary 5-8) -- never from incumbent chart imagery (evidence
// E6). Pure derivation over the SERVED rows; the engine is untouched.

const FDI_QUADRANT_NAMES: Readonly<Record<string, string>> = {
  '1': 'Upper right',
  '2': 'Upper left',
  '3': 'Lower left',
  '4': 'Lower right',
  '5': 'Upper right',
  '6': 'Upper left',
  '7': 'Lower left',
  '8': 'Lower right',
};

/**
 * DOCTOR-CONSULT-DEMO-POLISH-V1 (item 5d): the marker rendered beside each
 * quadrant name is the anatomical QUADRANT INDEX, not a count -- FDI digits
 * 1-4 (permanent) and 5-8 (primary) name the SAME four quadrants (the
 * FDI_QUADRANT_NAMES table above already reuses "Upper right" for both '1'
 * and '5'), so both dentitions map onto the one Q1-Q4 index. The former
 * "(1x)" rendering read as ambiguous demo copy (not a count, not obviously
 * an index either); Q1-Q4 states the meaning plainly.
 */
const FDI_QUADRANT_INDEX: Readonly<Record<string, string>> = {
  '1': 'Q1',
  '2': 'Q2',
  '3': 'Q3',
  '4': 'Q4',
  '5': 'Q1',
  '6': 'Q2',
  '7': 'Q3',
  '8': 'Q4',
};

export interface ToothQuadrantGroup {
  /** The FDI quadrant digit ('1'..'8'). */
  readonly digit: string;
  /** "Upper right (Q1)" -- the standard name + the quadrant index. */
  readonly label: string;
  readonly teeth: readonly ToothReferenceView[];
}

/** Split one ALREADY-ORDERED arch row into its quadrant groups (order
 *  preserved; an unknown leading digit renders code-only, never invented). */
export function toothQuadrantGroups(
  archTeeth: readonly ToothReferenceView[],
): readonly ToothQuadrantGroup[] {
  const groups: Array<{ digit: string; teeth: ToothReferenceView[] }> = [];
  for (const tooth of archTeeth) {
    const digit = tooth.fdiCode.charAt(0);
    const last = groups[groups.length - 1];
    if (last !== undefined && last.digit === digit) {
      last.teeth.push(tooth);
    } else {
      groups.push({ digit, teeth: [tooth] });
    }
  }
  return groups.map((g) => {
    const name = FDI_QUADRANT_NAMES[g.digit];
    const index = FDI_QUADRANT_INDEX[g.digit] ?? g.digit;
    return {
      digit: g.digit,
      label: name !== undefined ? `${name} (${index})` : `(${index})`,
      teeth: g.teeth,
    };
  });
}

/** Toggle one canonical code in a selection (order-preserving add/remove). */
export function toggleToothCode(
  selected: readonly string[],
  toothCode: string,
): string[] {
  return selected.includes(toothCode)
    ? selected.filter((c) => c !== toothCode)
    : [...selected, toothCode];
}

// --- Quadrant selection (DOCTOR-QUADRANT-SELECT-V1) --------------------------
//
// THE MEASURED PROBLEM: planning a full-mouth scaling is 32 taps; a
// quadrant-wise plan is 8. A dentist does this several times a chair-day. The
// quadrant GROUPING above already existed and already rendered -- nothing
// listened to it. These three pure helpers are the whole engine; the caller
// still owns persistence, exactly as it does for toggleToothCode.
//
// PARITY IS THE CONTRACT (never a convenience): a quadrant tap must produce
// the SAME array a human gets by tapping that quadrant's teeth one at a time
// -- same shape, same order rule, no new field. A human never re-taps an
// ALREADY selected tooth (that would deselect it), so the add path appends
// only the codes the selection is missing, in the group's own render order --
// which is exactly what repeated toggleToothCode calls over the unselected
// members produce. Nothing here widens a read, infers, or translates: this is
// the same FACT-capture control, driven from the group instead of the member.

/**
 * True when EVERY tooth of the group is already selected. An empty group is
 * never "selected" -- there is nothing to clear.
 */
export function isQuadrantSelected(
  selected: readonly string[],
  quadrantTeeth: readonly ToothReferenceView[],
): boolean {
  return (
    quadrantTeeth.length > 0 &&
    quadrantTeeth.every((t) => selected.includes(t.toothCode))
  );
}

/**
 * Toggle a WHOLE quadrant. Every member selected -> clear exactly those;
 * otherwise -> select every member (so a PARTIAL quadrant completes on the
 * next tap and never clears out from under the doctor). Teeth OUTSIDE the
 * quadrant are untouched in BOTH directions and keep their relative order.
 */
export function toggleQuadrantCodes(
  selected: readonly string[],
  quadrantTeeth: readonly ToothReferenceView[],
): string[] {
  if (isQuadrantSelected(selected, quadrantTeeth)) {
    const codes = new Set(quadrantTeeth.map((t) => t.toothCode));
    return selected.filter((c) => !codes.has(c));
  }
  const next = [...selected];
  for (const tooth of quadrantTeeth) {
    if (!next.includes(tooth.toothCode)) next.push(tooth.toothCode);
  }
  return next;
}

/**
 * The quadrant control's accessible label: it names the group AND what the
 * next activation will DO, so a screen-reader user is never told only where
 * he is. Pairs with aria-pressed on the control itself.
 */
export function quadrantActionLabel(
  group: ToothQuadrantGroup,
  isSelected: boolean,
): string {
  return isSelected
    ? `Clear all teeth in ${group.label}`
    : `Select all teeth in ${group.label}`;
}

/**
 * Resolve one TYPED entry (the assistive-secondary path, ruling R2) against
 * the served set: an exact FDI token or an exact Universal token (case-
 * insensitive) resolves to its canonical code; anything else is null --
 * an invalid entry can never join the selection.
 */
export function resolveTypedTooth(
  references: readonly ToothReferenceView[],
  raw: string,
): ToothReferenceView | null {
  const token = raw.trim().toUpperCase();
  if (token === '') return null;
  return (
    references.find(
      (t) => t.fdiCode === token || t.universalCode.toUpperCase() === token,
    ) ?? null
  );
}

/** The selected codes resolved to served rows (unknown codes dropped),
 *  selection order preserved -- the chips render from this. */
export function selectedToothRefs(
  references: readonly ToothReferenceView[],
  selected: readonly string[],
): readonly ToothReferenceView[] {
  const byCode = new Map(references.map((t) => [t.toothCode, t]));
  const out: ToothReferenceView[] = [];
  for (const code of selected) {
    const ref = byCode.get(code);
    if (ref !== undefined) out.push(ref);
  }
  return out;
}

/** Set equality for tooth selections (order-insensitive -- the write is a
 *  full-set replace; a reorder alone is not a change worth a write). */
export function sameToothSet(
  a: readonly string[],
  b: readonly string[],
): boolean {
  if (a.length !== b.length) return false;
  const bs = new Set(b);
  return a.every((code) => bs.has(code));
}

/** The saved diagnosis-grain codes on a loaded sheet ([] when none/absent). */
export function sheetToothCodes(
  sheet: CaseSheetClinicalView | null,
): readonly string[] {
  return (sheet?.toothRefs ?? []).map((r) => r.toothCode);
}

/** Chip label for a SAVED (self-describing) tooth ref view. */
export function savedToothRefLabel(
  ref: ToothRefView,
  display: ToothNotationDisplayView,
): string {
  return `${display === 'universal' ? ref.universalCode : ref.fdiCode} - ${ref.canonicalName}`;
}

// --- Anatomical chart upgrade (DOCTOR-OS-EXPERIENCE-V1 section D) -----------
//
// The odontogram upgrade from a button-token grid to the OWNED anatomical
// 2D SVG chart (founder order section D; the honest-delivery ruling D10
// chose the owned 2D/SVG path). Every helper here stays PURE and derives
// each anatomical fact from the served FDI digits ONLY -- the frontend
// never imports lib/domain (the served wire is the one source, the same
// posture as the arch derivation above).

/** The four crown families the glyph set draws (occlusal/incisal view). */
export type ToothGlyphKind = 'incisor' | 'canine' | 'premolar' | 'molar';

/**
 * Glyph family for one tooth, derived from the FDI POSITION digit
 * (ISO 3950): 1-2 incisor, 3 canine, then 4-5 premolar / 6-8 molar for the
 * permanent dentition, while the primary dentition's 4-5 are its MOLARS
 * (a primary dentition has no premolars). Mirrors the derivation rule of
 * lib/domain/clinical/tooth-reference.ts toothType() WITHOUT importing it
 * (frontend consumes served rows only; lib/ is not an import target here).
 */
export function toothGlyphKind(
  toothCode: string,
  dentition: ToothDentitionView,
): ToothGlyphKind {
  const position = Number(toothCode.charAt(1));
  if (position >= 1 && position <= 2) return 'incisor';
  if (position === 3) return 'canine';
  if (dentition === 'primary') return 'molar';
  return position >= 4 && position <= 5 ? 'premolar' : 'molar';
}

/** Full-curve geometry for the arch layout. The component feeds these into
 *  per-tooth CSS custom properties; the stylesheet may FLATTEN them on
 *  narrow viewports (a curve multiplier), so these values are the
 *  full-width truth, never a per-viewport fact. */
export const TOOTH_ARCH_CURVE_DEPTH_PX = 16;
export const TOOTH_ARCH_CURVE_MAX_ROTATE_DEG = 7;

export interface ToothArchPlacement {
  /** CSS rotation in degrees; positive = clockwise. */
  readonly rotateDeg: number;
  /** CSS translateY in px; positive = down. */
  readonly offsetY: number;
}

function roundPlacement(value: number): number {
  const rounded = Math.round(value * 100) / 100;
  return rounded === 0 ? 0 : rounded; // never -0 (stable serialization)
}

/**
 * Placement of one tooth on the curved arch: `index` positions it within
 * the FULL viewer-order arch row of `count` teeth (the curve spans both
 * quadrant groups). The UPPER arch bows DOWNWARD at the midline (the smile
 * arc: central incisors sit lowest) and each crown tilts toward the arc's
 * center of curvature; the LOWER arch is the exact vertical mirror (bows
 * upward), so the two rows converge toward the visible midline gap the way
 * a frontal smile reads. Pure; symmetric across the arch midline.
 */
export function archToothPlacement(
  index: number,
  count: number,
  arch: 'upper' | 'lower',
): ToothArchPlacement {
  const t = count <= 1 ? 0 : (2 * index) / (count - 1) - 1; // -1 .. +1
  const bow = 1 - t * t; // 0 at the ends, 1 at the midline
  const rotate = TOOTH_ARCH_CURVE_MAX_ROTATE_DEG * t;
  const offset = TOOTH_ARCH_CURVE_DEPTH_PX * bow;
  return arch === 'upper'
    ? { rotateDeg: roundPlacement(-rotate), offsetY: roundPlacement(offset) }
    : { rotateDeg: roundPlacement(rotate), offsetY: roundPlacement(-offset) };
}

/**
 * OPTIONAL per-tooth overlay state (chart annotations). RENDER-ONLY facts
 * the caller already lawfully holds -- the diagram infers nothing and this
 * prop never widens a read (the clinical wire fence stays SELECT-level).
 * An absent map, absent entry, or empty object = today's plain rendering.
 */
export interface ToothOverlayView {
  readonly condition?: 'missing' | 'extracted' | 'unerupted';
  readonly treatment?: 'proposed' | 'accepted' | 'completed' | 'declined';
  readonly evidence?: boolean;
  readonly assignedElsewhere?: boolean;
}

/** Screen-reader parity (D5): the overlay state WORDS appended to a
 *  tooth's aria-label, e.g. "missing, treatment accepted, evidence
 *  attached". null = no overlay state (the label stays unchanged). */
export function toothOverlayLabel(
  overlay: ToothOverlayView | undefined,
): string | null {
  if (overlay === undefined) return null;
  const parts: string[] = [];
  if (overlay.condition !== undefined) parts.push(overlay.condition);
  if (overlay.treatment !== undefined) parts.push(`treatment ${overlay.treatment}`);
  if (overlay.evidence === true) parts.push('evidence attached');
  if (overlay.assignedElsewhere === true) parts.push('assigned elsewhere');
  return parts.length > 0 ? parts.join(', ') : null;
}

/** The closed legend vocabulary, in fixed render order. */
export type ToothOverlayLegendKey =
  | 'missing'
  | 'extracted'
  | 'unerupted'
  | 'proposed'
  | 'accepted'
  | 'completed'
  | 'declined'
  | 'evidence'
  | 'assigned-elsewhere';

export interface ToothOverlayLegendEntry {
  readonly key: ToothOverlayLegendKey;
  readonly label: string;
}

const TOOTH_OVERLAY_LEGEND: readonly ToothOverlayLegendEntry[] = [
  { key: 'missing', label: 'Missing' },
  { key: 'extracted', label: 'Extracted' },
  { key: 'unerupted', label: 'Unerupted' },
  { key: 'proposed', label: 'Treatment proposed' },
  { key: 'accepted', label: 'Treatment accepted' },
  { key: 'completed', label: 'Treatment completed' },
  { key: 'declined', label: 'Treatment declined' },
  { key: 'evidence', label: 'Evidence attached' },
  { key: 'assigned-elsewhere', label: 'Assigned elsewhere' },
];

/** The DISTINCT overlay states present across the supplied map, in the
 *  fixed legend order (labels, not sentences -- the render law). [] hides
 *  the legend row entirely, so a chart without overlays stays exactly the
 *  pre-upgrade surface. */
export function toothOverlayLegend(
  overlays: Readonly<Record<string, ToothOverlayView>> | undefined,
): readonly ToothOverlayLegendEntry[] {
  if (overlays === undefined) return [];
  const present = new Set<ToothOverlayLegendKey>();
  for (const overlay of Object.values(overlays)) {
    if (overlay === undefined) continue;
    if (overlay.condition !== undefined) present.add(overlay.condition);
    if (overlay.treatment !== undefined) present.add(overlay.treatment);
    if (overlay.evidence === true) present.add('evidence');
    if (overlay.assignedElsewhere === true) present.add('assigned-elsewhere');
  }
  return TOOTH_OVERLAY_LEGEND.filter((entry) => present.has(entry.key));
}
